import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { ChapterText, ContinueButton, Revelation, Whisper } from '../zen-ui';
import { RELAYS } from './field';

const FADING_INDEX = 1;

/** Chapter IV — one relay goes quiet. The others remember. */
export function FadingRelay({ onComplete }: { onComplete: () => void }) {
  const [gone, setGone] = useState(false);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    if (!gone) return;
    const t = window.setTimeout(() => setResolved(true), 1900);
    return () => clearTimeout(t);
  }, [gone]);

  return (
    <div className="relative z-10 flex min-h-dvh flex-col items-center justify-center gap-10 px-6 py-24 text-center">
      <ChapterText numeral="IV · resilience" title="nothing here is fragile.">
        Relays come and go like lanterns in the wind. When one goes dark, the
        others remember.
      </ChapterText>

      <div
        className="animate-zen-fade-in relative mx-auto h-72 w-full max-w-2xl sm:h-80"
        style={{ animationDelay: '700ms' }}
      >
        {RELAYS.map((r, i) => {
          const isFading = i === FADING_INDEX;
          const inner = (
            <div
              className={cn(
                'relative grid h-12 w-12 place-items-center rounded-full border transition-all ease-out',
                isFading
                  ? 'border-white/15 bg-white/[3%] duration-[2200ms]'
                  : 'border-amber-100/40 bg-amber-100/5 shadow-[0_0_30px_rgba(253,224,171,0.12)] duration-1000',
                isFading && gone && 'scale-75 opacity-0',
              )}
            >
              <div
                className={cn(
                  'h-2 w-2 rounded-full transition-all duration-1000',
                  isFading
                    ? 'bg-white/35'
                    : 'bg-amber-200 shadow-[0_0_16px_rgba(253,224,171,0.9)]',
                )}
              />
              {/* the copy of the note held by this relay */}
              {!isFading && (
                <div className="absolute -bottom-1 h-1 w-1 rounded-full bg-amber-200/80" />
              )}
            </div>
          );

          return (
            <div
              key={i}
              className={cn(
                'absolute -translate-x-1/2 -translate-y-1/2',
                isFading && !gone && 'animate-zen-flicker',
              )}
              style={{ left: `${r.x}%`, top: `${r.y}%` }}
            >
              {isFading && !gone ? (
                <button
                  type="button"
                  onClick={() => setGone(true)}
                  aria-label="let the fading relay go"
                  className="cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-100/60"
                >
                  {inner}
                </button>
              ) : (
                inner
              )}
            </div>
          );
        })}
      </div>

      {!resolved ? (
        <Whisper delay={1100}>
          {gone ? 'letting it go…' : 'one is going quiet… touch it, and let it go'}
        </Whisper>
      ) : (
        <div className="flex flex-col items-center gap-8">
          <Revelation delay={200}>
            nothing was lost. your words live on in the others.
          </Revelation>
          <ContinueButton onClick={onComplete} delay={1000} />
        </div>
      )}
    </div>
  );
}
